/**
 * ApplicationDetailPanel
 *
 * Expandable detail panel for a selected credit application.
 * Shows application summary, key metrics, submissions list,
 * and communication timeline in a tabbed layout.
 *
 * F006:
 * AC001: Application detail renders on selection
 * AC002: Submissions list within application
 * AC003: Record lender response
 * AC004: Communication timeline
 * AC005: Add communication
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  X,
  Plus,
  ArrowRight,
  ArrowLeft,
  MessageSquare,
  FileText,
  ClipboardCheck,
  Loader2,
} from "lucide-react";
import { CreateSubmissionModal } from "@/components/modals/create-submission-modal";
import { RecordResponseModal } from "@/components/modals/record-response-modal";
import { AddCommunicationModal } from "@/components/modals/add-communication-modal";
import { ConditionManagementPanel } from "@/components/deals/condition-management-panel";

// ── Types ────────────────────────────────────────────────────────────────────

interface ApplicationRow {
  id: string;
  dealId: string;
  applicationReference: string | null;
  lenderReference: string | null;
  lenderId: string | null;
  lenderName: string | null;
  lenderProductId: string | null;
  applicationType: string;
  applicationStatus: string;
  requestedAmount: string | null;
  approvedAmount: string | null;
  facilityLimit: string | null;
  proposedRate: string | null;
  approvedRate: string | null;
  assessmentRate: string | null;
  createdDate: string | null;
  submittedDate: string | null;
  decisionDate: string | null;
  approvalExpiryDate: string | null;
  expectedSettlementDate: string | null;
  actualSettlementDate: string | null;
  servicingAtActualRate: string | null;
  servicingAtAssessmentRate: string | null;
  lvrTotal: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SubmissionRow {
  id: string;
  applicationId: string;
  submissionReference: string | null;
  submissionType: string;
  submissionMethod: string;
  submissionDate: string | null;
  creditAnalystName: string | null;
  executiveSummary: string | null;
  coverLetter: string | null;
  submissionNotes: string | null;
  responseReceived: number;
  responseDate: string | null;
  responseType: string | null;
  responseSummary: string | null;
  conditionsReceived: number;
  createdAt: string;
}

interface CommunicationRow {
  id: string;
  applicationId: string;
  submissionId: string | null;
  communicationType: string;
  direction: string;
  communicationDate: string;
  subject: string | null;
  content: string | null;
  summary: string | null;
  fromName: string | null;
  toNames: string | null;
  createdAt: string;
}

interface ApplicationDetailPanelProps {
  applicationId: string;
  dealId: string;
  application: ApplicationRow;
  onClose: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const APPLICATION_STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  pending_review: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200",
  conditionally_approved: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200",
  withdrawn: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  expired: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200",
};

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending Review",
  in_review: "In Review",
  approved: "Approved",
  conditionally_approved: "Conditionally Approved",
  declined: "Declined",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

const APPLICATION_TYPE_LABELS: Record<string, string> = {
  new: "New",
  variation: "Variation",
  refinance: "Refinance",
  top_up: "Top Up",
  restructure: "Restructure",
};

const SUBMISSION_TYPE_LABELS: Record<string, string> = {
  initial: "Initial",
  resubmission: "Resubmission",
  variation: "Variation",
  appeal: "Appeal",
};

const SUBMISSION_METHOD_LABELS: Record<string, string> = {
  portal: "Portal",
  email: "Email",
  broker_platform: "Broker Platform",
  api: "API",
};

const RESPONSE_STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-800",
  approved: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  conditional: "bg-yellow-100 text-yellow-800",
  more_info: "bg-blue-100 text-blue-800",
};

const RESPONSE_TYPE_LABELS: Record<string, string> = {
  approved: "Approved",
  declined: "Declined",
  conditional: "Conditional",
  more_info: "More Info",
};

const COMMUNICATION_TYPE_LABELS: Record<string, string> = {
  submission: "Submission",
  query: "Query",
  response: "Response",
  update: "Update",
  approval: "Approval",
  decline: "Decline",
  condition_request: "Condition Request",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatRate(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  // Rates stored as decimal (e.g. 0.0435 = 4.35%)
  return `${(num * 100).toFixed(2)}%`;
}

function formatPercent(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${num.toFixed(2)}%`;
}

// ── Component ────────────────────────────────────────────────────────────────

export function ApplicationDetailPanel({
  applicationId,
  application,
  onClose,
}: ApplicationDetailPanelProps) {
  const [showCreateSubmission, setShowCreateSubmission] = useState(false);
  const [respondingSubmission, setRespondingSubmission] = useState<SubmissionRow | null>(null);
  const [showAddCommunication, setShowAddCommunication] = useState(false);

  // Fetch submissions for this application
  const { data: submissions = [], isLoading: submissionsLoading } = useQuery<SubmissionRow[]>({
    queryKey: [`/api/applications/${applicationId}/submissions`],
    enabled: !!applicationId,
  });

  // Fetch communications for this application
  const { data: communications = [], isLoading: communicationsLoading } = useQuery<CommunicationRow[]>({
    queryKey: [`/api/applications/${applicationId}/communications`],
    enabled: !!applicationId,
  });

  // Sort communications by date DESC
  const sortedCommunications = [...communications].sort(
    (a, b) => new Date(b.communicationDate).getTime() - new Date(a.communicationDate).getTime(),
  );

  return (
    <Card className="mt-4" data-testid="application-detail-panel">
      <CardContent className="pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Application Detail</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            data-testid="button-close-detail"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Two-column summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Left: Application Summary */}
          <div className="space-y-2 text-sm" data-testid="application-summary">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Reference</p>
                <p>{application.applicationReference || "-"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Lender</p>
                <p>{application.lenderName || "No lender"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Type</p>
                <p>{APPLICATION_TYPE_LABELS[application.applicationType] || application.applicationType}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Status</p>
                <Badge
                  variant="secondary"
                  className={`text-xs ${APPLICATION_STATUS_COLORS[application.applicationStatus] || ""}`}
                >
                  {APPLICATION_STATUS_LABELS[application.applicationStatus] || application.applicationStatus}
                </Badge>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Submitted</p>
                <p>{formatDate(application.submittedDate)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Decision</p>
                <p>{formatDate(application.decisionDate)}</p>
              </div>
            </div>
            {application.notes && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">Notes</p>
                <p className="whitespace-pre-wrap text-xs">{application.notes}</p>
              </div>
            )}
          </div>

          {/* Right: Key Metrics */}
          <div className="space-y-2 text-sm" data-testid="application-metrics">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Requested</p>
                <p className="font-medium">{formatCurrency(application.requestedAmount)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Approved</p>
                <p className="font-medium">{formatCurrency(application.approvedAmount)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Proposed Rate</p>
                <p>{formatRate(application.proposedRate)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Approved Rate</p>
                <p>{formatRate(application.approvedRate)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Assessment Rate</p>
                <p>{formatRate(application.assessmentRate)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">LVR</p>
                <p>{formatPercent(application.lvrTotal)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Servicing (Actual)</p>
                <p>{formatPercent(application.servicingAtActualRate)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Servicing (Assessment)</p>
                <p>{formatPercent(application.servicingAtAssessmentRate)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed content: Submissions + Communications */}
        <Tabs defaultValue="submissions" className="mt-2">
          <TabsList>
            <TabsTrigger value="submissions" data-testid="tab-submissions">
              <FileText className="mr-1 h-3.5 w-3.5" />
              Submissions
            </TabsTrigger>
            <TabsTrigger value="communications" data-testid="tab-communications">
              <MessageSquare className="mr-1 h-3.5 w-3.5" />
              Communications
            </TabsTrigger>
            <TabsTrigger value="conditions" data-testid="tab-conditions">
              <ClipboardCheck className="mr-1 h-3.5 w-3.5" />
              Conditions
            </TabsTrigger>
          </TabsList>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCreateSubmission(true)}
                data-testid="button-create-submission"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                New Submission
              </Button>
            </div>

            {submissionsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : submissions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4" data-testid="empty-submissions">
                No submissions yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Response</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((sub) => (
                    <TableRow key={sub.id} data-testid={`submission-row-${sub.id}`}>
                      <TableCell className="text-xs">
                        {sub.submissionReference || "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {SUBMISSION_TYPE_LABELS[sub.submissionType] || sub.submissionType}
                      </TableCell>
                      <TableCell className="text-xs">
                        {SUBMISSION_METHOD_LABELS[sub.submissionMethod] || sub.submissionMethod}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDate(sub.submissionDate)}
                      </TableCell>
                      <TableCell>
                        {sub.responseReceived ? (
                          <Badge
                            variant="secondary"
                            className={`text-xs ${RESPONSE_STATUS_COLORS[sub.responseType || ""] || "bg-gray-100 text-gray-800"}`}
                            data-testid={`response-badge-${sub.responseType}`}
                          >
                            {RESPONSE_TYPE_LABELS[sub.responseType || ""] || sub.responseType || "Responded"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-800">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!sub.responseReceived && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setRespondingSubmission(sub)}
                            data-testid={`button-record-response-${sub.id}`}
                          >
                            Record Response
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {communications.length} communication{communications.length !== 1 ? "s" : ""}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddCommunication(true)}
                data-testid="button-add-communication"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add Communication
              </Button>
            </div>

            {communicationsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : sortedCommunications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4" data-testid="empty-communications">
                No communications yet.
              </p>
            ) : (
              <div className="space-y-0" data-testid="communication-timeline">
                {sortedCommunications.map((comm) => (
                  <div
                    key={comm.id}
                    className="flex items-start gap-3 py-3 border-b last:border-b-0"
                    data-testid={`communication-entry-${comm.id}`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {comm.direction === "outbound" ? (
                        <ArrowRight className="h-4 w-4 text-blue-500" data-testid="icon-outbound" />
                      ) : (
                        <ArrowLeft className="h-4 w-4 text-green-500" data-testid="icon-inbound" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs" data-testid={`comm-type-badge-${comm.communicationType}`}>
                          {COMMUNICATION_TYPE_LABELS[comm.communicationType] || comm.communicationType}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(comm.communicationDate)}
                        </span>
                      </div>
                      <p className="text-sm font-medium mt-1">{comm.subject || "(No subject)"}</p>
                      {comm.summary && (
                        <p className="text-xs text-muted-foreground mt-0.5">{comm.summary}</p>
                      )}
                      {comm.fromName && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          From: {comm.fromName}
                          {comm.toNames ? ` | To: ${comm.toNames}` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Conditions Tab */}
          <TabsContent value="conditions">
            <ConditionManagementPanel applicationId={applicationId} />
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Modals */}
      <CreateSubmissionModal
        open={showCreateSubmission}
        onOpenChange={setShowCreateSubmission}
        applicationId={applicationId}
      />

      {respondingSubmission && (
        <RecordResponseModal
          open={!!respondingSubmission}
          onOpenChange={(open) => !open && setRespondingSubmission(null)}
          submissionId={respondingSubmission.id}
          applicationId={applicationId}
        />
      )}

      <AddCommunicationModal
        open={showAddCommunication}
        onOpenChange={setShowAddCommunication}
        applicationId={applicationId}
      />
    </Card>
  );
}
